import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardContent,
    CardHeader,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Grid,
    Divider,
    Box,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
    Paper,
    InputAdornment,
    Popover,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Switch,
    Icon
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
    Storage as StorageIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Settings as SettingsIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    FolderOpen as FolderIcon,
    Remove as CollapseIcon,
    ExpandLess as ExpandLessIcon,
    ArrowDropDown as ArrowDropDownIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { 
    getStorageProviders,
    fetchStorageProviderPolicies,
    selectVolumePolicy
} from 'app/main/cluster/store/actions/cluster.actions';
import {
    selectProviderPoliciesById,
    selectProviderPoliciesLoading,
    selectProviderPoliciesLoadingById,
    selectProviderPoliciesErrorById,
    selectSelectedPolicyById,
    selectPolicyConstraintsByProviderAndPolicy,
    selectFilteredInstanceTypes,
    selectAllowedVolumeSizes,
    selectConfigurationValidationById,
    selectValidationErrors,
    selectPolicyUIState
} from 'app/main/cluster/store/selectors';
import InstanceTypeSelector from './InstanceTypeSelector';
import PolicyDetailsDialog from './PolicyDetailsDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    cardHeader: {
        paddingBottom: '8px',
        backgroundColor: '#f7f9fc'
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    formSection: {
        marginBottom: theme.spacing(3)
    },
    advancedSettings: {
        backgroundColor: theme.palette.grey[25],
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    },
    providerChip: {
        marginLeft: theme.spacing(1)
    },
    infoIcon: {
        color: theme.palette.info.main,
        fontSize: '1.2rem'
    },
    addButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        width: '100%',
        minHeight: theme.spacing(8),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
    storageConfigCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        transition: 'all 0.3s ease',
    },
    storageConfigHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    storageConfigContent: {
        padding: theme.spacing(2),
    },
    noProvidersAlert: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    settingsLink: {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    expandIcon: {
        fontSize: 16,
        color: theme.palette.text.secondary
    },
    fadeInAnimation: {
        animation: '$fadeIn 1.05s ease-in-out'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0.5
        },
        '100%': {
            opacity: 1
        }
    },
    updateButton: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        transition: 'all 0.3s ease',
        '&.disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[500],
            cursor: 'not-allowed',
            '&:hover': {
                backgroundColor: theme.palette.grey[300],
            }
        },
        '&.active': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            }
        }
    },
    spinnerIcon: {
        animation: '$spin 1s linear infinite'
    },
    '@keyframes spin': {
        '0%': {
            transform: 'rotate(0deg)'
        },
        '100%': {
            transform: 'rotate(360deg)'
        }
    }
}));

const StorageConfigurationSection = ({ form, setForm, disabled, zones, onStorageConfigurationChange, cloudProvider }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);
    
    // Redux selectors with defensive programming
    const allStorageProviders = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.storageProviders) || []);
    
    // Filter providers based on cloud provider compatibility
    const storageProviders = React.useMemo(() => {
        if (!cloudProvider) {
            return allStorageProviders;
        }
        
        return allStorageProviders.filter(provider => {
            // Volumez is only available for AWS
            if (provider.type === 'volumez') {
                return cloudProvider === 'aws';
            }
            
            // Heidi AI (IBM storage) is only available for IBM
            if (provider.type === 'heidi-ai') {
                return cloudProvider === 'ibm';
            }
            
            // For other provider types, check cloudProvider field directly
            return provider.cloudProvider === cloudProvider || !provider.cloudProvider;
        });
    }, [allStorageProviders, cloudProvider]);
    const storageProvidersLoading = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.storageProvidersLoading) || false);
    const storageProvidersError = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.storageProvidersError) || null);
    const providers = useSelector(({ providers }) => providers.providers.list);
    const stacks = useSelector(({ stacks }) => stacks.stack.list);
    
    // Volumez Policy Redux selectors
    const providerPolicies = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.providerPolicies) || {});
    const providerPoliciesLoading = useSelector(selectProviderPoliciesLoading);
    const selectedPolicies = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.selectedPolicies) || {});
    const policyConstraints = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.policyConstraints) || {});
    const configurationValidation = useSelector(({ clusters }) => (clusters && clusters.cluster && clusters.cluster.configurationValidation) || {});
    
    // Compute available zones with fallback to extracting from providers
    const availableZones = zones || (() => {
        if (form?.data?.network?.region_id && providers?.length > 0) {
            const awsProvider = providers.find(provider => provider.id === "aws");
            if (awsProvider?.regions) {
                const currentRegion = awsProvider.regions.find(r => r.region_id === form.data.network.region_id);
                return currentRegion?.zones || [];
            }
        }
        return [];
    })();
    
    // Local state
    const [storageConfigurations, setStorageConfigurations] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [expandedConfigs, setExpandedConfigs] = useState({});
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [mediaInstanceSizes, setMediaInstanceSizes] = useState([]);
    const [stackMenuAnchorEl, setStackMenuAnchorEl] = useState({});
    const [activeStackConfigId, setActiveStackConfigId] = useState(null);
    const [newlyAddedConfigs, setNewlyAddedConfigs] = useState(new Set());
    const animationTimeouts = useRef(new Map());

    // Validation state for infrastructure requirements
    const [showInfrastructureValidation, setShowInfrastructureValidation] = useState(false);

    // Check if infrastructure region and zone are selected
    const isInfrastructureConfigured = Boolean(form?.data?.network?.region_id && form?.data?.network?.zone_id);
    const canAddStorageProvider = isInfrastructureConfigured && !disabled && selectedProvider;

    // Delete confirmation dialog state
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);
    
    // Policy details dialog state
    const [policyDetailsDialogOpen, setPolicyDetailsDialogOpen] = useState(false);
    const [policyDetailsProviderId, setPolicyDetailsProviderId] = useState(null);

    // Update volumes button state
    const [isUpdatingVolumes, setIsUpdatingVolumes] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);
    const [originalConfigurations, setOriginalConfigurations] = useState([]);
    
    // Volumez Policy Management state
    const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
    const [selectedConfigForPolicy, setSelectedConfigForPolicy] = useState(null);
    
    // Count validation warnings state
    const [countWarnings, setCountWarnings] = useState({});

    useEffect(() => {
        // Fetch storage providers using Redux action
        dispatch(getStorageProviders());
        
        // Populate media instance sizes from AWS provider data - HPC families only
        if (providers && providers.length > 0) {
            const awsProvider = providers.find(provider => provider.id === "aws");
            if (awsProvider && awsProvider.instanceSizes) {
                // Specs lookup for HPC instances (i8g, i4g, is4gen, i4i, i3en families)
                // ARM64 instances: i8g (Graviton3), i4g (Graviton2), is4gen (Graviton2)
                // x86_64 instances: i4i (Intel Ice Lake), i3en (Intel Skylake/Cascade Lake)
                const instanceSpecs = {
                    // i8g family (arm64 Graviton3)
                    'i8g.xlarge': { vcpu: 4, memory: 32, storage: '3750GB' },
                    'i8g.2xlarge': { vcpu: 8, memory: 64, storage: '7500GB' },
                    'i8g.4xlarge': { vcpu: 16, memory: 128, storage: '15000GB' },
                    'i8g.8xlarge': { vcpu: 32, memory: 256, storage: '30000GB' },
                    'i8g.16xlarge': { vcpu: 64, memory: 512, storage: '60000GB' },
                    
                    // i4g family (arm64 Graviton2)
                    'i4g.large': { vcpu: 2, memory: 16, storage: '937GB' },
                    'i4g.xlarge': { vcpu: 4, memory: 32, storage: '1875GB' },
                    'i4g.2xlarge': { vcpu: 8, memory: 64, storage: '1875GB' },
                    'i4g.4xlarge': { vcpu: 16, memory: 128, storage: '3750GB' },
                    'i4g.8xlarge': { vcpu: 32, memory: 256, storage: '7500GB' },
                    'i4g.16xlarge': { vcpu: 64, memory: 512, storage: '15000GB' },
                    
                    // is4gen family (arm64 Graviton2)
                    'is4gen.medium': { vcpu: 1, memory: 6, storage: '937GB' },
                    'is4gen.large': { vcpu: 2, memory: 12, storage: '1875GB' },
                    'is4gen.xlarge': { vcpu: 4, memory: 24, storage: '3492GB' },
                    'is4gen.2xlarge': { vcpu: 8, memory: 48, storage: '6984GB' },
                    'is4gen.4xlarge': { vcpu: 16, memory: 96, storage: '13968GB' },
                    'is4gen.8xlarge': { vcpu: 32, memory: 192, storage: '27936GB' },
                    
                    // i4i family (x86_64 Intel Ice Lake) - AMD64 equivalent
                    'i4i.large': { vcpu: 2, memory: 16, storage: '468GB' },
                    'i4i.xlarge': { vcpu: 4, memory: 32, storage: '937GB' },
                    'i4i.2xlarge': { vcpu: 8, memory: 64, storage: '1875GB' },
                    'i4i.4xlarge': { vcpu: 16, memory: 128, storage: '3750GB' },
                    'i4i.8xlarge': { vcpu: 32, memory: 256, storage: '7500GB' },
                    'i4i.16xlarge': { vcpu: 64, memory: 512, storage: '15000GB' },
                    'i4i.32xlarge': { vcpu: 128, memory: 1024, storage: '30000GB' },
                    
                    // i3en family (x86_64 Intel Skylake/Cascade Lake) - AMD64 equivalent
                    'i3en.large': { vcpu: 2, memory: 16, storage: '1250GB' },
                    'i3en.xlarge': { vcpu: 4, memory: 32, storage: '2500GB' },
                    'i3en.2xlarge': { vcpu: 8, memory: 64, storage: '5000GB' },
                    'i3en.3xlarge': { vcpu: 12, memory: 96, storage: '7500GB' },
                    'i3en.6xlarge': { vcpu: 24, memory: 192, storage: '15000GB' },
                    'i3en.12xlarge': { vcpu: 48, memory: 384, storage: '30000GB' },
                    'i3en.24xlarge': { vcpu: 96, memory: 768, storage: '60000GB' }
                };
                
                // Filter to show HPC instance families: i8g, i4g, is4gen (ARM64) + i4i, i3en (x86_64)
                const mediaInstances = awsProvider.instanceSizes.filter(instance => {
                    const instanceType = instance.instance_id;
                    return instanceType.startsWith('i8g.') || 
                           instanceType.startsWith('i4g.') || 
                           instanceType.startsWith('is4gen.') ||
                           instanceType.startsWith('i4i.') ||
                           instanceType.startsWith('i3en.');
                }).map(instance => {
                    // Add architecture information based on instance family
                    const instanceType = instance.instance_id;
                    let architecture;
                    
                    // ARM64 instances (AWS Graviton processors)
                    if (instanceType.startsWith('i8g.')) {
                        architecture = 'arm64'; // Graviton3
                    } else if (instanceType.startsWith('i4g.') || instanceType.startsWith('is4gen.')) {
                        architecture = 'arm64'; // Graviton2
                    }
                    // x86_64 instances (Intel processors)
                    else if (instanceType.startsWith('i4i.') || instanceType.startsWith('i3en.')) {
                        architecture = 'x86_64'; // Intel Ice Lake / Skylake
                    }
                    
                    // Get specs from lookup table
                    const specs = instanceSpecs[instanceType] || {};
                    
                    return {
                        ...instance,
                        architecture,
                        // Add proper fields for specs display
                        vcpu: specs.vcpu || instance.cores,
                        memory: specs.memory,
                        storage: specs.storage,
                        // Update name to include architecture - use instance_id to avoid duplication
                        name: `${instance.instance_id} (${architecture})`
                    };
                });
                
                // Sort instances by family and size for better UX
                mediaInstances.sort((a, b) => {
                    const aFamily = a.instance_id.split('.')[0];
                    const bFamily = b.instance_id.split('.')[0];
                    
                    // Sort by family first (i8g, i4g, is4gen), then by size
                    if (aFamily !== bFamily) {
                        const familyOrder = { 'i8g': 1, 'i4g': 2, 'is4gen': 3 };
                        return familyOrder[aFamily] - familyOrder[bFamily];
                    }
                    
                    // Within same family, sort by size (xlarge, 2xlarge, 4xlarge)
                    return a.instance_id.localeCompare(b.instance_id);
                });
                
                setMediaInstanceSizes(mediaInstances);
                
       
            } else {
                console.warn('StorageConfigurationSection: AWS provider not found or missing instanceSizes in providers.json');
                setMediaInstanceSizes([]);
            }
        }
        
        // Initialize storage configurations from form data if they exist
        if (form && form.data && form.data.storageConfigurations) {
            // Ensure all configurations have valid IDs, stackName field, and proper resiliency defaults
            const configsWithIds = form.data.storageConfigurations.map((config, index) => {
                // Helper function to derive basePath and volumePrefix from mountingPath
                const deriveFromMountingPath = (mountingPath) => {
                    if (!mountingPath) return { basePath: '/mnt', volumePrefix: 'storage' };
                    
                    // Match pattern like "/mnt/demo-*" or "/mnt/storage-*"
                    const match = mountingPath.match(/^(.+)\/([^/]+)-\*$/);
                    if (match) {
                        return { basePath: match[1], volumePrefix: match[2] };
                    }
                    
                    // Fallback: extract from path structure
                    const pathParts = mountingPath.split('/');
                    if (pathParts.length >= 2) {
                        const lastPart = pathParts[pathParts.length - 1];
                        const basePath = pathParts.slice(0, -1).join('/') || '/mnt';
                        const volumePrefix = lastPart.replace(/-\d+$/, '') || 'storage';
                        return { basePath, volumePrefix };
                    }
                    
                    return { basePath: '/mnt', volumePrefix: 'storage' };
                };

                return {
                    ...config,
                    id: config.id || `existing-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
                    stackName: config.stackName || '', // Ensure stackName exists
                    count: config.count || 1, // Ensure count exists with default value
                    mediaZoneId: config.mediaZoneId || form?.data?.network?.zone_id || null, // Default to infrastructure zone if not set
                    
                    // Set selectedPolicy from stored value or volumezSettings.policyName
                    selectedPolicy: config.selectedPolicy || config.volumezSettings?.policyName || (() => {
                        // Fallback to first provider policy or 'custom'
                        const currentProvider = storageProviders.find(p => p._id === config.providerId);
                        const policies = currentProvider?.policies || [];
                        return policies.length > 0 ? policies[0].name : 'custom';
                    })(),
                    
                    // Ensure volume config exists with new fields
                    volumeConfig: (() => {
                        const existing = config.volumeConfig || {};
                        let basePath = existing.basePath;
                        let volumePrefix = existing.volumePrefix;
                        
                        // If basePath or volumePrefix are missing, try to derive from mountingPath
                        if (!basePath || !volumePrefix) {
                            const derived = deriveFromMountingPath(config.mountingPath);
                            basePath = basePath || derived.basePath;
                            volumePrefix = volumePrefix || derived.volumePrefix || 
                                config.name?.toLowerCase().replace(/\s+/g, '-') || 'storage';
                        }
                        
                        return {
                            ...existing,
                            size: existing.size || 500,
                            basePath: basePath || '/mnt',
                            volumePrefix: volumePrefix || 'storage'
                        };
                    })(),
                    
                    // Ensure resiliency config exists and has proper defaults (convert null to 0, like bandwidth field pattern)
                    resiliencyConfig: {
                        mediaFailures: (config.resiliencyConfig?.mediaFailures !== undefined && config.resiliencyConfig.mediaFailures !== null) ? config.resiliencyConfig.mediaFailures : 0,
                        nodeFailures: (config.resiliencyConfig?.nodeFailures !== undefined && config.resiliencyConfig.nodeFailures !== null) ? config.resiliencyConfig.nodeFailures : 0,
                        zoneFailures: (config.resiliencyConfig?.zoneFailures !== undefined && config.resiliencyConfig.zoneFailures !== null) ? config.resiliencyConfig.zoneFailures : 0,
                        ...(config.resiliencyConfig || {})
                    },
                    // Ensure performance config exists with backward compatibility
                    performanceConfig: {
                        // New read/write performance fields - set defaults if not present
                        readIops: config.performanceConfig?.readIops || config.performanceConfig?.iops || 8000,
                        readBandwidth: config.performanceConfig?.readBandwidth || config.performanceConfig?.bandwidth || 1000,
                        writeIops: config.performanceConfig?.writeIops || config.performanceConfig?.iops || 8000,
                        writeBandwidth: config.performanceConfig?.writeBandwidth || config.performanceConfig?.bandwidth || 1000,
                        // Keep legacy fields for backward compatibility
                        iops: config.performanceConfig?.iops || 20000,
                        bandwidth: config.performanceConfig?.bandwidth || 500,
                        ...(config.performanceConfig || {})
                    }
                    // Note: Removed replicas field - storage configurations are now templates
                };
            });

            setStorageConfigurations(configsWithIds);
            
            // Store original configurations for change detection
            setOriginalConfigurations(JSON.parse(JSON.stringify(configsWithIds)));
            
            // Update the form with the initialized configurations to ensure new fields are propagated
            updateFormStorageConfigs(configsWithIds);
            
            // Initialize all existing configs as minimized (collapsed) by default
            const initialExpandedState = {};
            configsWithIds.forEach(config => {
                initialExpandedState[config.id] = false; // Set to false to start collapsed
            });
            setExpandedConfigs(initialExpandedState);
            
            // Update form data with the configs that now have IDs and stackName
            if (configsWithIds.some((config, index) => 
                config.id !== form.data.storageConfigurations[index]?.id || 
                config.stackName !== form.data.storageConfigurations[index]?.stackName
            )) {
                updateFormStorageConfigs(configsWithIds);
            }
        }        // Cleanup function to prevent memory leaks
        return () => {
            isMountedRef.current = false;
            // Clear all animation timeouts
            animationTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
            animationTimeouts.current.clear();
        };
    }, [dispatch, providers]);

    // Effect to fetch provider policies when storage providers are loaded
    useEffect(() => {
        if (storageProviders?.length > 0) {
            storageProviders.forEach(provider => {
                // Always fetch policies to ensure we have the latest data
                // This will help pick up any changes to the policy JSON files
                dispatch(fetchStorageProviderPolicies(provider._id));
            });
        }
    }, [dispatch, storageProviders]);

    // Effect to detect changes in storage configurations
    useEffect(() => {
        if (originalConfigurations.length > 0 && storageConfigurations.length > 0) {
            // Deep comparison to detect any changes
            const hasChanges = JSON.stringify(originalConfigurations.sort((a, b) => a.id.localeCompare(b.id))) !== 
                              JSON.stringify(storageConfigurations.sort((a, b) => a.id.localeCompare(b.id)));
            setHasUnsavedChanges(hasChanges);
            setUpdateButtonDisabled(!hasChanges || isUpdatingVolumes);
        } else if (storageConfigurations.length !== originalConfigurations.length) {
            // Handle case where configurations are added or removed
            const hasChanges = storageConfigurations.length > 0;
            setHasUnsavedChanges(hasChanges);
            setUpdateButtonDisabled(!hasChanges || isUpdatingVolumes);
        } else {
            setHasUnsavedChanges(false);
            setUpdateButtonDisabled(true);
        }
    }, [storageConfigurations, originalConfigurations, isUpdatingVolumes]);

    // Update configurations when zones become available or infrastructure zone changes
    useEffect(() => {
        if (form?.data?.network?.zone_id) {
            // Update configurations that don't have an explicit zone set to use the infrastructure zone
            setStorageConfigurations(prevConfigs => {
                if (prevConfigs.length === 0) return prevConfigs;
                
                const updatedConfigs = prevConfigs.map(config => {
                    if (!config.mediaZoneId) {
                        return {
                            ...config,
                            mediaZoneId: form.data.network.zone_id
                        };
                    }
                    return config;
                });
                
                // Only update if there were changes
                const hasChanges = updatedConfigs.some((config, index) => 
                    config.mediaZoneId !== prevConfigs[index].mediaZoneId
                );
                
                if (hasChanges) {
                    updateFormStorageConfigs(updatedConfigs);
                    return updatedConfigs;
                }
                
                return prevConfigs;
            });
        }
    }, [form?.data?.network?.zone_id, availableZones]);

    // Monitor volume additions/removals in node groups and update media node counts dynamically
    useEffect(() => {
        if (!form?.data?.node?.group) return;

        const nodeGroups = form.data.node.group;
        const volumeCountByConfig = {};

        // Count volumes for each storage configuration across all node groups
        nodeGroups.forEach((group, idx) => {
            if (group.attachedVolumes && Array.isArray(group.attachedVolumes)) {
                group.attachedVolumes.forEach(volume => {
                    const configId = volume.storageConfigId;
                    if (configId) {
                        volumeCountByConfig[configId] = (volumeCountByConfig[configId] || 0) + 1;
                    }
                });
            }
        });

        // Also count volumes attached to the head node (server)
        if (form.data.server?.attachedVolumes && Array.isArray(form.data.server.attachedVolumes)) {
            form.data.server.attachedVolumes.forEach(volume => {
                const configId = volume.storageConfigId;
                if (configId) {
                    volumeCountByConfig[configId] = (volumeCountByConfig[configId] || 0) + 1;
                }
            });
        }

        // Update media node counts for each configuration using functional update
        setStorageConfigurations(prevConfigs => {
            if (!prevConfigs.length) return prevConfigs;

            let hasChanges = false;
            const updatedConfigs = prevConfigs.map(config => {
                const volumeCount = volumeCountByConfig[config.id] || 0;
                
                // Only update if:
                // 1. Has a non-custom policy
                // 2. Has selected media type
                // 3. Has volumes attached
                if (config.selectedPolicy && config.selectedPolicy !== 'custom' && config.mediaType && volumeCount > 0) {
                    // Calculate required media nodes based on volume count
                    const policies = providerPolicies[config.providerId];
                    if (!policies) return config;

                    const selectedPolicyData = policies.find(p => p.name === config.selectedPolicy);
                    if (!selectedPolicyData) return config;

                    // Get maxVolumesPerMedia from policy (default to 1 if not set)
                    const maxVolumesPerMedia = selectedPolicyData.maxVolumesPerMedia || 1;
                    
                    // Get base node count from policy configuration
                    const matchingConfig = selectedPolicyData.allowedConfigurations?.find(
                        conf => conf.instanceType === config.mediaType
                    );
                    const baseNodeCount = matchingConfig?.nodeCount || 4;

                    // Calculate required media node sets
                    const mediaNodeSets = Math.ceil(volumeCount / maxVolumesPerMedia);
                    const requiredMediaNodes = mediaNodeSets * baseNodeCount;

                    if (config.count !== requiredMediaNodes) {
                        hasChanges = true;
                        return { ...config, count: requiredMediaNodes };
                    }
                }
                
                return config;
            });

            if (hasChanges) {
                updateFormStorageConfigs(updatedConfigs);
                return updatedConfigs;
            }
            
            return prevConfigs;
        });
    }, [
        form?.data?.node?.group, 
        form?.data?.node?.group?.length, 
        JSON.stringify(form?.data?.node?.group?.map(g => g.attachedVolumes?.length)), 
        form?.data?.server?.attachedVolumes,
        form?.data?.server?.attachedVolumes?.length,
        providerPolicies
    ]);

    const updateFormStorageConfigs = (configs) => {
        if (!isMountedRef.current) return;
        
        // Clone configs and ensure proper persistence mapping
        const configsForPersistence = configs.map(config => {
            const clonedConfig = { ...config };
            
            // Generate mountingPath from basePath and volumePrefix for backend compatibility
            const basePath = config.volumeConfig?.basePath || '/mnt';
            const volumePrefix = (config.volumeConfig?.volumePrefix && config.volumeConfig.volumePrefix.trim()) || 
                                (config.name?.toLowerCase().replace(/\s+/g, '-')) || 'storage';
            clonedConfig.mountingPath = `${basePath}/${volumePrefix}-*`;
            
            // Ensure volumezSettings exists and policyName is synced with selectedPolicy
            if (config.providerType === 'volumez' || config.provider === 'volumez') {
                if (!clonedConfig.volumezSettings) clonedConfig.volumezSettings = {};
                clonedConfig.volumezSettings.policyName = config.selectedPolicy || 
                    clonedConfig.volumezSettings.policyName || 'custom';
            }
            
            return clonedConfig;
        });
        
        const updatedForm = { ...form };
        if (!updatedForm.data) updatedForm.data = {};
        updatedForm.data.storageConfigurations = configsForPersistence;
        setForm(updatedForm);
        
        // Validate storage configurations
        const validateStorageConfigurations = (configurations) => {
            if (!configurations || configurations.length === 0) return { isValid: true, errors: [] };
            
            const errors = [];
            let allConfigsValid = true;
            
            configurations.forEach((config, index) => {
                const configPrefix = `Configuration "${config.name || `${index + 1}`}"`;
                
                // Check if name is required and valid
                const hasValidName = config.name && config.name.trim() !== '';
                if (!hasValidName) {
                    errors.push(`${configPrefix}: Name is required`);
                    allConfigsValid = false;
                }
                
                // Use API-based validation from Redux state (single source of truth)
                if (config.providerId && config.selectedPolicy && config.selectedPolicy !== 'custom') {
                    const validation = configurationValidation[config.providerId];
                    
                    if (validation && validation.isValid === false) {
                        const validationErrors = validation.errors || [];
                        validationErrors.forEach(error => {
                            errors.push(`${configPrefix}: ${error}`);
                        });
                        allConfigsValid = false;
                    }
                    
                    // Note: Instance type and volume size validation is handled by the API
                    // through the validateConfiguration Redux action. No client-side duplication.
                }
                
                // Check for required media configuration fields
                if (config.mediaType && (!config.stackName || config.stackName.trim() === '')) {
                    errors.push(`${configPrefix}: Stack selection required when media instance type is specified`);
                    allConfigsValid = false;
                }
                
                // Check for required volume configuration fields
                if (!config.volumeConfig?.size || config.volumeConfig.size <= 0) {
                    errors.push(`${configPrefix}: Volume size must be greater than 0`);
                    allConfigsValid = false;
                }
            });
            
            return { isValid: allConfigsValid, errors };
        };
        
        const validationResult = validateStorageConfigurations(configs);
        
        // Notify parent component about storage configuration changes
        // Focus on media configurations since compute target logic is moved to form.js
        if (onStorageConfigurationChange) {
            // Filter for complete media configurations (both mediaType and stackName required)
            const completeMediaConfigurations = configs.filter(config => 
                config.mediaType && 
                config.mediaType.trim() !== '' && 
                config.stackName && 
                config.stackName.trim() !== ''
            );
            
            // Format complete media configurations with calculated node count based on policy + resiliency
            const formattedMediaConfigs = completeMediaConfigurations.map(config => {
                // Determine required nodes from selected policy and instance type when available
                let requiredPolicyCount = 1;
                if (config.selectedPolicy && config.selectedPolicy !== 'custom' && config.mediaType) {
                    try {
                        requiredPolicyCount = calculateRequiredCount(
                            config.selectedPolicy,
                            config.mediaType,
                            config.resiliencyConfig,
                            config.providerId
                        ) || 1;
                    } catch (e) {
                        requiredPolicyCount = 1;
                    }
                }

                // Respect user-specified count but never go below policy minimum
                const baseCount = config.count || 1;
                const requiredMediaNodes = Math.max(baseCount, requiredPolicyCount);
                
                return {
                    id: config.id,
                    name: config.volumeConfig?.name || `Volumez Storage ${config.id}`,
                    provider: config.providerType,
                    mediaType: config.mediaType,
                    mediaZoneId: config.mediaZoneId || null,
                    stackName: config.stackName || '',
                    storageConfigId: config.id,
                    nodeType: 'media',
                    // Add calculated node count for automatic injection
                    requiredNodes: requiredMediaNodes,
                    count: baseCount, // Pass through the base count
                    resiliencyConfig: config.resiliencyConfig,
                    // Pass through volumez settings for compute target determination in form.js
                    volumezSettings: config.volumezSettings
                };
            });

            // Always pass configurations to parent, even if no complete media configs exist
            // This ensures proper cleanup of orphaned node groups when configurations are removed
            // or when media configurations become incomplete
            onStorageConfigurationChange(formattedMediaConfigs, {
                hasStorageConfigurations: configs.length > 0,
                allConfigs: configs, // Pass all configs for form.js to determine compute targets
                completedMediaConfigs: completeMediaConfigurations.length,
                totalConfigs: configs.length,
                validation: validationResult // Pass validation status to parent
            });
        }
    };

    const addStorageConfiguration = () => {
        if (!isMountedRef.current || !selectedProvider) return;
        
        // Check if infrastructure (region and zone) is configured
        if (!isInfrastructureConfigured) {
            setShowInfrastructureValidation(true);
            // Hide the validation message after 5 seconds
            setTimeout(() => {
                setShowInfrastructureValidation(false);
            }, 5000);
            return;
        }
        
        const provider = storageProviders.find(p => p._id === selectedProvider);
        if (!provider) return;

        // Generate a truly unique ID using timestamp + random number
        const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Generate 4 random characters for the default name
        const randomChars = Math.random().toString(36).substr(2, 4).toUpperCase();
        const defaultName = `Storage-${randomChars}`;

        const newConfig = {
            id: uniqueId,
            name: defaultName, // Default name with Storage- prefix and 4 random chars
            providerId: selectedProvider,
            providerName: provider.name,
            providerType: provider.type,
            mediaType: '', // Start blank - node group will be added when media type is selected
            mediaZoneId: form?.data?.network?.zone_id || null, // Default to infrastructure zone
            stackName: '', // Start blank - user will select the stack for media nodes
            count: 1, // Default to 1 media node
            volumeConfig: {
                size: 500, // Default size in GB
                basePath: '/mnt', // Base mount path
                volumePrefix: defaultName.toLowerCase().replace(/\s+/g, '-') // Volume prefix based on configuration name
            },
            performanceConfig: {
                // Read performance settings (following convention from image)
                readIops: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.readIops) || 8000,
                readBandwidth: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.readBandwidth) || 1000,
                // Write performance settings (following convention from image)
                writeIops: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.writeIops) || 8000,
                writeBandwidth: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.writeBandwidth) || 1000,
                
                // Legacy fields for backward compatibility
                iops: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.iops) || 20000,
                bandwidth: (provider.configuration && provider.configuration.defaultPerformance && provider.configuration.defaultPerformance.bandwidth) || 500
            },
            // Default policy selection - smart defaults based on available policies
            selectedPolicy: (() => {
                const provider = storageProviders.find(p => p._id === selectedProvider);
                const policies = provider?.policies || [];
                // If provider has policies, select the first one; otherwise select custom
                return policies.length > 0 ? policies[0].name : 'custom';
            })(),
            // Resiliency configuration with default values (minimal for 1 media node)
            resiliencyConfig: {
                mediaFailures: 0, // Default: minimal configuration for 1 media node
                nodeFailures: 0,  // Default: minimal configuration for 1 media node
                zoneFailures: 0   // Default: minimal configuration for 1 media node
            },
            // Volumez-specific settings - simplified since compute target is moved
            ...(provider.type === 'volumez' && {
                volumezSettings: {
                    policyName: (() => {
                        const provider = storageProviders.find(p => p._id === selectedProvider);
                        const policies = provider?.policies || [];
                        return policies.length > 0 ? policies[0].name : 'custom';
                    })(), // Sync with selectedPolicy
                    attachmentTarget: 'compute-nodes', // Will be determined by form.js logic
                    attachToHeadNode: false, // Default to false
                    autoAssignMedia: true,
                    waitForAttachment: true,
                    debug: false
                }
            })
        };

        const updatedConfigs = [...storageConfigurations, newConfig];
        setStorageConfigurations(updatedConfigs);
        updateFormStorageConfigs(updatedConfigs);
        setSelectedProvider(''); // Reset selection
        
        // Minimize all existing configurations and expand only the newly added one
        setExpandedConfigs({
            [newConfig.id]: true // Only the new config is expanded
        });
        
        // Track newly added config for animation
        setNewlyAddedConfigs(prev => new Set([...prev, newConfig.id]));
        
        // Remove from newly added configs after animation completes
        const timeoutId = setTimeout(() => {
            if (isMountedRef.current) {
                setNewlyAddedConfigs(prev => {
                    const updated = new Set(prev);
                    updated.delete(newConfig.id);
                    return updated;
                });
                animationTimeouts.current.delete(newConfig.id);
            }
        }, 1050); // Match animation duration (30% faster)
        
        animationTimeouts.current.set(newConfig.id, timeoutId);
    };

    const toggleConfigExpansion = (configId) => {
        if (!isMountedRef.current) return;
        setExpandedConfigs(prev => ({
            ...prev,
            [configId]: !prev[configId]
        }));
    };

    const removeStorageConfiguration = (configId) => {
        if (!isMountedRef.current) return;
        
        // Find the configuration to check for attached volumes and media nodes
        const configToRemove = storageConfigurations.find(config => config.id === configId);
        if (!configToRemove) return;
        
        // Show confirmation dialog instead of immediate deletion
        setConfigToDelete(configToRemove);
        setConfirmDeleteDialogOpen(true);
    };

    const showDeleteConfirmation = (configId) => {
        const configToRemove = storageConfigurations.find(config => config.id === configId);
        if (configToRemove) {
            setConfigToDelete(configToRemove);
            setConfirmDeleteDialogOpen(true);
        }
    };

    const closeConfirmDeleteDialog = () => {
        setConfirmDeleteDialogOpen(false);
        setConfigToDelete(null);
    };

    const confirmDeleteStorageConfiguration = () => {
        if (!configToDelete || !isMountedRef.current) return;
        
        const configId = configToDelete.id;
        const updatedConfigs = storageConfigurations.filter(config => config.id !== configId);
        setStorageConfigurations(updatedConfigs);
        updateFormStorageConfigs(updatedConfigs);
        
        // Clean up menu state for removed config
        setStackMenuAnchorEl(prev => {
            const newState = { ...prev };
            delete newState[configId];
            return newState;
        });
        if (activeStackConfigId === configId) {
            setActiveStackConfigId(null);
        }
        
        // Clean up newly added configs state
        setNewlyAddedConfigs(prev => {
            const updated = new Set(prev);
            updated.delete(configId);
            return updated;
        });
        
        // Clean up animation timeout if it exists
        const timeoutId = animationTimeouts.current.get(configId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            animationTimeouts.current.delete(configId);
        }
        
        closeConfirmDeleteDialog();
    };

    const updateStorageConfiguration = (configId, path, value) => {
        if (!isMountedRef.current) return;
        
        setStorageConfigurations(prevConfigs => {
            const updatedConfigs = prevConfigs.map(config => {
                if (config.id !== configId) return config;
                
                const pathArray = path.split('.');
                const updatedConfig = { ...config };
                let current = updatedConfig;
                
                for (let i = 0; i < pathArray.length - 1; i++) {
                    if (!current[pathArray[i]]) current[pathArray[i]] = {};
                    current = current[pathArray[i]];
                }
                current[pathArray[pathArray.length - 1]] = value;
                
                // Special handling for selectedPolicy - also update volumezSettings.policyName
                if (path === 'selectedPolicy') {
                    if (!updatedConfig.volumezSettings) updatedConfig.volumezSettings = {};
                    updatedConfig.volumezSettings.policyName = value;
                }
                
                return updatedConfig;
            });
            
            updateFormStorageConfigs(updatedConfigs);
            return updatedConfigs;
        });
    };

    // Helper function to calculate required count based on policy and resiliency
    const calculateRequiredCount = (policyName, instanceType, resiliencyConfig, providerId) => {
        if (policyName === 'custom' || !policyName || !instanceType) return 1;

        const policies = providerPolicies[providerId];
        if (!policies) {
            return 1;
        }

        const selectedPolicyData = policies.find(p => p.name === policyName);
        if (!selectedPolicyData || !selectedPolicyData.allowedConfigurations) {
            return 1;
        }

        const matchingConfig = selectedPolicyData.allowedConfigurations.find(
            conf => conf.instanceType === instanceType
        );
        let basePolicyCount;
        if (matchingConfig && matchingConfig.nodeCount) {
            basePolicyCount = matchingConfig.nodeCount;
        } else {
            // Fallback: if the selected instance type isn't explicitly listed,
            // enforce the minimum nodeCount from the policy's allowedConfigurations
            const counts = selectedPolicyData.allowedConfigurations
                .map(c => c.nodeCount)
                .filter(n => typeof n === 'number' && n > 0);
            basePolicyCount = counts.length ? Math.min(...counts) : 1;
        }
        

        
        // The policy nodeCount values already include the default resiliency multipliers
        // We just return the policy count directly
        return basePolicyCount;
    };

    // Helper function to check if manual count conflicts with policy requirements
    const validateCountAgainstPolicy = (count, policyName, instanceType, resiliencyConfig, providerId) => {
        const requiredCount = calculateRequiredCount(policyName, instanceType, resiliencyConfig, providerId);
        
        if (policyName === 'custom') return { isValid: true, requiredCount };
        
        return {
            isValid: count >= requiredCount,
            requiredCount,
            hasConflict: count < requiredCount
        };
    };

    // Volumez Policy Management handlers
    const handlePolicySelect = async (configId, policyName) => {
        const config = storageConfigurations.find(c => c.id === configId);
        if (!config) return;

        // Update local state immediately
        updateStorageConfiguration(configId, 'selectedPolicy', policyName);
        
        // Update Redux state
        dispatch(selectVolumePolicy(config.providerId, policyName));
        
        // Note: Policy data including allowedConfigurations is already loaded via fetchStorageProviderPolicies
        // The selectors will extract the constraints from the policy data automatically
        
        // If policy is selected, sync resiliency settings and update count
        if (policyName !== 'custom') {
            const policies = providerPolicies[config.providerId];
            if (policies) {
                const selectedPolicyData = policies.find(p => p.name === policyName);
                if (selectedPolicyData) {
                    // Sync resiliency settings from policy to UI
                    const policyResiliency = {
                        mediaFailures: selectedPolicyData.resiliencymedia || 0,
                        nodeFailures: selectedPolicyData.resiliencynode || 0,
                        zoneFailures: selectedPolicyData.resiliencyzone || 0
                    };
                    
                    updateStorageConfiguration(configId, 'resiliencyConfig', policyResiliency);
                    
                    // Update count if instance type is already selected
                    if (config.mediaType) {
                        const requiredCount = calculateRequiredCount(
                            policyName, 
                            config.mediaType, 
                            policyResiliency, 
                            config.providerId
                        );
                        updateStorageConfiguration(configId, 'count', requiredCount);
                    }
                }
            }
        }
        
        // Clear any count warnings when policy changes
        setCountWarnings(prev => {
            const newWarnings = { ...prev };
            delete newWarnings[configId];
            return newWarnings;
        });
        
        // Note: Configuration validation is now handled locally using the policy data
        // No need for separate API validation calls since we have all constraint data loaded
    };

    const handleOpenPolicyDialog = (configId) => {
        setSelectedConfigForPolicy(configId);
        setPolicyDialogOpen(true);
    };

    const handleClosePolicyDialog = () => {
        setPolicyDialogOpen(false);
        setSelectedConfigForPolicy(null);
    };

    // Stack menu handlers
    const handleStackMenuOpen = (event, configId) => {
        setStackMenuAnchorEl(prev => ({ ...prev, [configId]: event.currentTarget }));
        setActiveStackConfigId(configId);
    };

    const handleStackMenuClose = (configId) => {
        setStackMenuAnchorEl(prev => ({ ...prev, [configId]: null }));
        setActiveStackConfigId(null);
    };

    const handleStackSelect = (configId, stackName) => {
        updateStorageConfiguration(configId, 'stackName', stackName);
        handleStackMenuClose(configId);
    };

    // Function to check if there are attached volumes for storage configurations
    const hasAttachedVolumes = () => {
        if (!form || !form.data || !form.data.node || !form.data.node.group) return false;
        
        const storageConfigIds = new Set(storageConfigurations.map(config => config.id));
        
        // Check all node groups for attached volumes
        for (const group of form.data.node.group) {
            if (group.attachedVolumes && Array.isArray(group.attachedVolumes)) {
                for (const volume of group.attachedVolumes) {
                    if (volume && volume.storageConfigId && storageConfigIds.has(volume.storageConfigId)) {
                        return true;
                    }
                }
            }
        }
        
        return false;
    };

    // Function to check if a specific configuration has attached volumes
    const hasAttachedVolumesForConfig = (configId) => {
        if (!form || !form.data || !form.data.node || !form.data.node.group) return false;
        
        // Check all node groups for attached volumes matching this config ID
        for (const group of form.data.node.group) {
            if (group.attachedVolumes && Array.isArray(group.attachedVolumes)) {
                for (const volume of group.attachedVolumes) {
                    if (volume && volume.storageConfigId === configId) {
                        return true;
                    }
                }
            }
        }
        
        return false;
    };

    // Function to check if there are unsaved changes for a specific configuration
    const hasUnsavedChangesForConfig = (configId) => {
        const currentConfig = storageConfigurations.find(config => config.id === configId);
        const originalConfig = originalConfigurations.find(config => config.id === configId);
        
        if (!currentConfig || !originalConfig) return false;
        
        return JSON.stringify(currentConfig) !== JSON.stringify(originalConfig);
    };

    // Function to update volumes for a specific configuration
    const handleUpdateVolumesForConfig = async (configId) => {
        if (!isMountedRef.current || isUpdatingVolumes || !hasUnsavedChangesForConfig(configId)) return;
        
        setIsUpdatingVolumes(true);
        
        try {
            // Simulate update process with 1.5-second delay for UX
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            const configToUpdate = storageConfigurations.find(config => config.id === configId);
            if (!configToUpdate) return;
            
            // Update form to apply changes to attached volumes for this specific configuration
            if (form && form.data && form.data.node && form.data.node.group) {
                const updatedForm = { ...form };
                let volumesUpdated = false;
                
                updatedForm.data.node.group.forEach((group, groupIndex) => {
                    if (group.attachedVolumes && group.attachedVolumes.length > 0) {
                        group.attachedVolumes.forEach((volume, volumeIndex) => {
                            // Only update volumes that match this specific configuration ID
                            if (volume.storageConfigId === configId) {
                                // Determine policy name and get appropriate policy values
                                const policyName = configToUpdate.volumezSettings?.policyName || configToUpdate.policyName || volume.policyName || 'custom';
                                let policyValues = {};
                                
                                if (policyName === 'custom') {
                                    // For custom policies, use values from configToUpdate (current behavior)
                                    policyValues = {
                                        readIops: configToUpdate.performanceConfig?.readIops,
                                        readBandwidth: configToUpdate.performanceConfig?.readBandwidth,
                                        writeIops: configToUpdate.performanceConfig?.writeIops,
                                        writeBandwidth: configToUpdate.performanceConfig?.writeBandwidth,
                                        latencyRead: configToUpdate.performanceConfig?.latencyRead,
                                        latencyWrite: configToUpdate.performanceConfig?.latencyWrite,
                                        resiliencyMedia: configToUpdate.resiliencyConfig?.mediaFailures || 0,
                                        resiliencyNode: configToUpdate.resiliencyConfig?.nodeFailures || 0,
                                        resiliencyZone: configToUpdate.resiliencyConfig?.zoneFailures || 0
                                    };
                                } else {
                                    // For predefined policies, look up policy values from storage provider
                                    const storageProvider = storageProviders.find(provider => provider._id === configToUpdate.providerId);
                                    let predefinedPolicy = null;
                                    
                                    if (storageProvider && storageProvider.policies) {
                                        // Try to find policy by name first, then by id if provided
                                        predefinedPolicy = storageProvider.policies.find(policy => 
                                            policy.name === policyName || 
                                            (configToUpdate.policyId && policy.id === configToUpdate.policyId)
                                        );
                                    }
                                    
                                    if (predefinedPolicy) {
                                        // Use values from the predefined policy
                                        policyValues = {
                                            readIops: predefinedPolicy.iopsread,
                                            readBandwidth: predefinedPolicy.bandwidthread,
                                            writeIops: predefinedPolicy.iopswrite,
                                            writeBandwidth: predefinedPolicy.bandwidthwrite,
                                            latencyRead: predefinedPolicy.latencyread,
                                            latencyWrite: predefinedPolicy.latencywrite,
                                            resiliencyMedia: predefinedPolicy.resiliencymedia,
                                            resiliencyNode: predefinedPolicy.resiliencynode,
                                            resiliencyZone: predefinedPolicy.resiliencyzone
                                        };
                                    } else {
                                        // Fallback to configToUpdate values if policy not found
                                        policyValues = {
                                            readIops: configToUpdate.performanceConfig?.readIops,
                                            readBandwidth: configToUpdate.performanceConfig?.readBandwidth,
                                            writeIops: configToUpdate.performanceConfig?.writeIops,
                                            writeBandwidth: configToUpdate.performanceConfig?.writeBandwidth,
                                            latencyRead: configToUpdate.performanceConfig?.latencyRead,
                                            latencyWrite: configToUpdate.performanceConfig?.latencyWrite,
                                            resiliencyMedia: configToUpdate.resiliencyConfig?.mediaFailures || 0,
                                            resiliencyNode: configToUpdate.resiliencyConfig?.nodeFailures || 0,
                                            resiliencyZone: configToUpdate.resiliencyConfig?.zoneFailures || 0
                                        };
                                    }
                                }
                                
                                // Update volume configuration based on storage template
                                const updatedVolume = {
                                    ...volume,
                                    size: configToUpdate.volumeConfig?.size || volume.size,
                                    performanceOverrides: {
                                        readIops: policyValues.readIops,
                                        readBandwidth: policyValues.readBandwidth,
                                        writeIops: policyValues.writeIops,
                                        writeBandwidth: policyValues.writeBandwidth,
                                        latencyRead: policyValues.latencyRead,
                                        latencyWrite: policyValues.latencyWrite
                                    },
                                    policyName: policyName,
                                    policyId: configToUpdate.policyId, // Store policy ID for predefined policies
                                    resiliencyConfig: {
                                        mediaFailures: policyValues.resiliencyMedia,
                                        nodeFailures: policyValues.resiliencyNode,
                                        zoneFailures: policyValues.resiliencyZone
                                    },
                                    advancedConfig: {
                                        ...volume.advancedConfig,
                                        localZoneRead: configToUpdate.advancedConfig?.localZoneRead,
                                        encryption: configToUpdate.advancedConfig?.encryption
                                    },
                                    capacityConfig: {
                                        ...volume.capacityConfig,
                                        optimization: configToUpdate.capacityConfig?.optimization,
                                        reservation: configToUpdate.capacityConfig?.reservation
                                    }
                                };
                                
                                updatedForm.data.node.group[groupIndex].attachedVolumes[volumeIndex] = updatedVolume;
                                volumesUpdated = true;
                            }
                        });
                    }
                });
                
                if (volumesUpdated) {
                    setForm(updatedForm);
                }
            }
            
            // Update the original configuration for this specific config to match current state
            setOriginalConfigurations(prev => 
                prev.map(config => 
                    config.id === configId 
                        ? JSON.parse(JSON.stringify(configToUpdate))
                        : config
                )
            );
            
        } catch (error) {
            console.error('Error updating volumes for configuration:', error);
        } finally {
            if (isMountedRef.current) {
                setIsUpdatingVolumes(false);
            }
        }
    };

    return (
        <Card className={classes.root}>
            <CardHeader
                className={classes.cardHeader}
                avatar={<StorageIcon />}
                title={
                    <Box display="flex" alignItems="center">
                        <Typography style={{ fontSize: '16px', fontWeight: 600, color: '#0f2147' }}>
                            High-Performance Data Infrastructure
                        </Typography>
                    </Box>
                }
            />
            <CardContent>
                {storageProviders.length === 0 ? (
                    <Alert 
                        severity="info" 
                        className={classes.noProvidersAlert}
                        action={
                            <Button
                                component="a"
                                href="/settings"
                                className={classes.settingsLink}
                                startIcon={<SettingsIcon />}
                                size="small"
                            >
                                Settings
                            </Button>
                        }
                    >
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                No data infrastructure providers configured
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                To configure high-performance data infrastructure for your cluster, you need to first add providers in the Settings page.
                            </Typography>
                        </Box>
                    </Alert>
                ) : (
                    <Box>
                        {/* Add New Data Infrastructure */}
                        <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                            <FormControl variant="outlined" style={{ minWidth: 200, marginRight: 20 }}>
                                <InputLabel>Select Provider</InputLabel>
                                <Select
                                    value={selectedProvider}
                                    onChange={(e) => setSelectedProvider(e.target.value)}
                                    label="Select Provider"
                                    disabled={disabled}
                                >
                                    {storageProviders.map((provider) => (
                                        <MenuItem key={provider._id} value={provider._id}>
                                            <Box display="flex" alignItems="center">
                                                <StorageIcon style={{ marginRight: 8 }} />
                                                <Box>
                                                    <Typography variant="body2">{provider.name}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {provider.type.toUpperCase()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip 
                                title={!isInfrastructureConfigured ? "Please select Region and Availability Zone in Infrastructure section first" : "Add new storage configuration"}
                                placement="top"
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={addStorageConfiguration}
                                        disabled={!canAddStorageProvider}
                                    >
                                        Add
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>

                        {/* Infrastructure validation message */}
                        {(showInfrastructureValidation || (!isInfrastructureConfigured && selectedProvider)) && (
                            <Alert 
                                severity="warning" 
                                style={{ marginTop: '16px', marginBottom: '16px' }}
                                onClose={() => setShowInfrastructureValidation(false)}
                            >
                                <Typography variant="body2" gutterBottom>
                                    Please select Region and Availability Zone in the Infrastructure section first
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    High-Performance Data Infrastructure requires a specific region and availability zone to be configured.
                                </Typography>
                            </Alert>
                        )}

                        {/* Existing storage configurations */}
                        {storageConfigurations
                            .map((config, index) => {
                            // Ensure we have a valid ID for the key
                            const configId = config.id || `fallback-${index}-${Date.now()}`;
                            const isExpanded = expandedConfigs[configId] === true; // Explicitly check for true
                            const isNewlyAdded = newlyAddedConfigs.has(configId);
                            
                            return (
                            <Paper 
                                key={`storage-config-${configId}`} 
                                className={`${classes.storageConfigCard} ${isNewlyAdded ? classes.fadeInAnimation : ''}`}
                                elevation={1}
                            >
                                {/* Provider Header */}
                                <Box 
                                    className={classes.storageConfigHeader}
                                    onClick={() => toggleConfigExpansion(configId)}
                                >
                                    <Box display="flex" alignItems="center">
                                        <StorageIcon style={{ marginRight: 8 }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            {config.name || config.providerName}
                                        </Typography>
                                        <Chip 
                                            label={config.providerType.toUpperCase()} 
                                            size="small" 
                                            className={classes.providerChip}
                                            variant="outlined"
                                            color="primary"
                                        />
                                        {!isExpanded && (
                                            <Box key={`collapsed-info-${configId}`} display="flex" alignItems="center" marginLeft={2}>
                                                <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                                                    •
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {config.volumeConfig?.size || 500}GB
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, marginRight: 8 }}>
                                                    • R: {config.performanceConfig?.readIops || 8000} IOPS, {config.performanceConfig?.readBandwidth || 1000} MiB/s
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, marginRight: 8 }}>
                                                    • W: {config.performanceConfig?.writeIops || 8000} IOPS, {config.performanceConfig?.writeBandwidth || 1000} MiB/s
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {`${config.volumeConfig?.basePath || '/mnt'}/${config.volumeConfig?.volumePrefix || 'storage'}-*`}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleConfigExpansion(configId);
                                            }}
                                            disabled={disabled}
                                            title={isExpanded ? "Minimize" : "Expand"}
                                        >
                                            {isExpanded ? <CollapseIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeStorageConfiguration(configId);
                                            }}
                                            disabled={disabled}
                                            title="Delete"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Configuration Content - Collapsible */}
                                <Collapse in={isExpanded}>
                                    <Box className={classes.storageConfigContent}>
                                <Grid container spacing={3}>
                                    {/* Configuration Name */}
                                    <Grid item xs={12} key={`config-name-${configId}`}>
                                        <TextField
                                            fullWidth
                                            label="Configuration Name"
                                            value={config.name || ''}
                                            onChange={(e) => updateStorageConfiguration(configId, 'name', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={disabled}
                                            size="small"
                                            required
                                            error={!config.name || config.name.trim() === ''}
                                            helperText={!config.name || config.name.trim() === '' ? 
                                                "Configuration name is required" : 
                                                "Enter a descriptive name for this storage configuration"
                                            }
                                        />
                                    </Grid>
                                    
                                    {/* Volumez Policy Configuration */}
                                    <Grid item xs={12} key={`policy-header-${configId}`}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography variant="subtitle2" gutterBottom color="primary">
                                                <SettingsIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                                Storage Policy Configuration
                                            </Typography>
                                            {providerPolicies[config.providerId] && providerPolicies[config.providerId].length > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPolicyDetailsProviderId(config.providerId);
                                                        setPolicyDetailsDialogOpen(true);
                                                    }}
                                                >
                                                    View All Policies
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} key={`policy-select-${configId}`}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Volume Policy</InputLabel>
                                            <Select
                                                value={(() => {
                                                    const val = config.selectedPolicy || 'custom';
                                                    return val;
                                                })()}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handlePolicySelect(configId, e.target.value);
                                                }}
                                                disabled={disabled}
                                                displayEmpty
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {/* Custom policy option always available */}
                                                <MenuItem value="custom">
                                                    Custom Policy (No constraints)
                                                </MenuItem>
                                                
                                                {/* Provider-specific policies */}
                                                {(() => {
                                                    const provider = storageProviders.find(p => p._id === config.providerId);
                                                    const policies = providerPolicies[config.providerId] || [];
                                                    
                                                    return policies.map(policy => (
                                                        <MenuItem key={policy.name} value={policy.name}>
                                                            {policy.displayName || policy.name} 
                                                            {policy.description && (
                                                                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                                                                    - {policy.description}
                                                                </Typography>
                                                            )}
                                                        </MenuItem>
                                                    ));
                                                })()}
                                            </Select>
                                            {(() => {
                                                const selectedPolicy = config.selectedPolicy;
                                                const validation = configurationValidation[config.providerId];
                                                
                                                if (selectedPolicy && selectedPolicy !== 'custom') {
                                                    if (validation?.isValid === false) {
                                                        return (
                                                            <FormHelperText error>
                                                                Configuration does not meet policy requirements
                                                            </FormHelperText>
                                                        );
                                                    } else if (validation?.isValid === true) {
                                                        return (
                                                            <FormHelperText style={{ color: 'green' }}>
                                                                Configuration meets policy requirements
                                                            </FormHelperText>
                                                        );
                                                    }
                                                }
                                                
                                                return (
                                                    <FormHelperText>
                                                        {selectedPolicy === 'custom' 
                                                            ? 'Custom policy allows any configuration'
                                                            : 'Select a volume policy to apply constraints'
                                                        }
                                                    </FormHelperText>
                                                );
                                            })()}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} key={`policy-info-${configId}`}>
                                        {(() => {
                                            const selectedPolicy = config.selectedPolicy;
                                            const constraints = policyConstraints[config.providerId]?.[selectedPolicy];
                                            
                                            if (selectedPolicy && selectedPolicy !== 'custom' && constraints) {
                                                return (
                                                    <Paper 
                                                        style={{ 
                                                            padding: '8px 12px', 
                                                            backgroundColor: '#f5f5f5',
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                                            Policy Constraints:
                                                        </Typography>
                                                        <Typography variant="body2" style={{ fontSize: '0.75rem' }}>
                                                            • Instance Types: {constraints.allowedConfigurations?.map(c => c.instanceType).join(', ') || 'Any'}
                                                        </Typography>
                                                        <Typography variant="body2" style={{ fontSize: '0.75rem' }}>
                                                            • Volume Sizes: {constraints.allowedConfigurations?.[0]?.allowedVolumeSizes?.join(', ') || 'Any'} GB
                                                        </Typography>
                                                    </Paper>
                                                );
                                            }
                                            
                                            return null;
                                        })()}
                                    </Grid>
                                    
                                    {/* Policy Validation Summary */}
                                    {(() => {
                                        const selectedPolicy = config.selectedPolicy;
                                        const validation = configurationValidation[config.providerId];
                                        const constraints = policyConstraints[config.providerId]?.[selectedPolicy];
                                        
                                        // Check for constraint violations
                                        const violations = [];
                                        
                                        if (selectedPolicy && selectedPolicy !== 'custom' && config.mediaType && constraints) {
                                            // Check instance type violation
                                            const allowedInstanceTypes = constraints.allowedConfigurations?.map(c => c.instanceType) || [];
                                            if (allowedInstanceTypes.length > 0 && !allowedInstanceTypes.includes(config.mediaType)) {
                                                violations.push({
                                                    type: 'instance',
                                                    message: `Instance type "${config.mediaType}" not allowed by "${selectedPolicy}" policy`,
                                                    suggestion: `Try: ${allowedInstanceTypes.slice(0, 3).join(', ')}`
                                                });
                                            }
                                            
                                            // Check volume size violation
                                            if (config.volumeConfig?.size && config.mediaType) {
                                                const instanceConfig = constraints.allowedConfigurations?.find(
                                                    c => c.instanceType === config.mediaType
                                                );
                                                if (instanceConfig?.allowedVolumeSizes?.length > 0) {
                                                    const allowedSizes = instanceConfig.allowedVolumeSizes;
                                                    if (!allowedSizes.includes(config.volumeConfig.size)) {
                                                        violations.push({
                                                            type: 'volume',
                                                            message: `Volume size ${config.volumeConfig.size}GB not allowed for ${config.mediaType}`,
                                                            suggestion: `Allowed sizes: ${allowedSizes.join(', ')}GB`
                                                        });
                                                    }
                                                }
                                            }
                                        }
                                        
                                        // Show validation summary if there are violations or successful validation
                                        if (violations.length > 0 || (validation?.isValid === true && selectedPolicy !== 'custom')) {
                                            return (
                                                <Grid item xs={12} key={`validation-summary-${configId}`}>
                                                    <Paper 
                                                        style={{ 
                                                            padding: '12px 16px', 
                                                            marginBottom: '16px',
                                                            backgroundColor: violations.length > 0 ? '#ffebee' : '#e8f5e8',
                                                            border: `1px solid ${violations.length > 0 ? '#f44336' : '#4caf50'}`
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="flex-start">
                                                            <Icon 
                                                                style={{ 
                                                                    color: violations.length > 0 ? '#f44336' : '#4caf50',
                                                                    marginRight: 8,
                                                                    fontSize: '1.2rem'
                                                                }}
                                                            >
                                                                {violations.length > 0 ? 'error' : 'check_circle'}
                                                            </Icon>
                                                            <Box>
                                                                <Typography 
                                                                    variant="subtitle2" 
                                                                    style={{ 
                                                                        color: violations.length > 0 ? '#f44336' : '#4caf50',
                                                                        fontWeight: 600,
                                                                        marginBottom: violations.length > 0 ? 8 : 0
                                                                    }}
                                                                >
                                                                    {violations.length > 0 
                                                                        ? `Policy Constraint Violations (${violations.length})`
                                                                        : `Configuration meets "${selectedPolicy}" policy requirements`
                                                                    }
                                                                </Typography>
                                                                {violations.map((violation, index) => (
                                                                    <Box key={index} style={{ marginBottom: index < violations.length - 1 ? 8 : 0 }}>
                                                                        <Typography variant="body2" style={{ color: '#f44336', marginBottom: 2 }}>
                                                                            • {violation.message}
                                                                        </Typography>
                                                                        <Typography variant="caption" style={{ color: '#666', marginLeft: 8 }}>
                                                                            {violation.suggestion}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            );
                                        }
                                        
                                        return null;
                                    })()}
                                    
                                    {/* Media Configuration */}
                                    <Grid item xs={12} key={`media-header-${configId}`}>
                                        <Typography variant="subtitle2" gutterBottom color="primary">
                                            <StorageIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                            Media Configuration
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} key={`media-type-${configId}`}>
                                        <Box onClick={(e) => e.stopPropagation()}>
                                            <InstanceTypeSelector
                                                key={`instance-selector-${configId}`}
                                                label="Media Node Instance Type"
                                                value={config.mediaType || ''}
                                                onChange={(e, { value, isValid, instance }) => {
                                                    updateStorageConfiguration(configId, 'mediaType', value);
                                                    
                                                    // If policy is selected, update count based on policy + resiliency requirements
                                                    if (config.selectedPolicy && config.selectedPolicy !== 'custom') {
                                                        const requiredCount = calculateRequiredCount(
                                                            config.selectedPolicy, 
                                                            value, 
                                                            config.resiliencyConfig, 
                                                            config.providerId
                                                        );
                                                        updateStorageConfiguration(configId, 'count', requiredCount);

                                                        // Clear any count warnings when instance type changes
                                                        setCountWarnings(prev => {
                                                            const newWarnings = { ...prev };
                                                            delete newWarnings[configId];
                                                            return newWarnings;
                                                        });
                                                    }
                                                }}
                                                disabled={disabled}
                                                providerId={config.providerId}
                                                selectedPolicy={config.selectedPolicy}
                                                availableInstances={mediaInstanceSizes}
                                                required
                                                showConstraints={config.selectedPolicy !== 'custom'}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3} key={`media-zone-${configId}`}>
                                        <FormControl fullWidth size="small" onClick={(e) => e.stopPropagation()}>
                                            <InputLabel>Availability Zone</InputLabel>
                                            <Select
                                                value={config.mediaZoneId || form?.data?.network?.zone_id || ''}
                                                onChange={(e) => {
                                                    // When infrastructure zone is selected, set to null to use infrastructure zone
                                                    const value = e.target.value === form?.data?.network?.zone_id ? null : e.target.value;
                                                    updateStorageConfiguration(configId, 'mediaZoneId', value);
                                                }}
                                                disabled={disabled}
                                                displayEmpty
                                            >
                                                {/* Show infrastructure zone as the default selected option */}
                                                {form?.data?.network?.zone_id && (
                                                    <MenuItem value={form.data.network.zone_id}>
                                                        {availableZones?.find(z => z.zone_id === form.data.network.zone_id)?.name || form.data.network.zone_id} (Infrastructure Zone)
                                                    </MenuItem>
                                                )}
                                                {(availableZones || []).filter(zone => zone.zone_id !== form?.data?.network?.zone_id).map((zone, zoneIndex) => (
                                                    <MenuItem 
                                                        key={zoneIndex} 
                                                        value={zone.zone_id}
                                                        id={`media-zone-${zone.zone_id}-${configId}`}
                                                    >
                                                        {zone.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={3} key={`media-count-${configId}`}>
                                        <TextField
                                            fullWidth
                                            label="Count"
                                            type="number"
                                            value={config.count !== undefined ? config.count : ''}
                                            onChange={(e) => {
                                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                updateStorageConfiguration(configId, 'count', value);
                                                
                                                // Check if manual count conflicts with policy requirements
                                                if (value !== '' && config.selectedPolicy && config.selectedPolicy !== 'custom' && config.mediaType) {
                                                    const validation = validateCountAgainstPolicy(
                                                        value,
                                                        config.selectedPolicy,
                                                        config.mediaType,
                                                        config.resiliencyConfig,
                                                        config.providerId
                                                    );
                                                    
                                                    if (validation.hasConflict) {
                                                        setCountWarnings(prev => ({
                                                            ...prev,
                                                            [configId]: {
                                                                message: `Policy "${config.selectedPolicy}" requires minimum ${validation.requiredCount} nodes for ${config.mediaType} with current resiliency settings. Configuration may fail.`,
                                                                requiredCount: validation.requiredCount,
                                                                currentCount: value
                                                            }
                                                        }));
                                                    } else {
                                                        setCountWarnings(prev => {
                                                            const newWarnings = { ...prev };
                                                            delete newWarnings[configId];
                                                            return newWarnings;
                                                        });
                                                    }
                                                } else {
                                                    // Clear warning for this config
                                                    setCountWarnings(prev => {
                                                        const newWarnings = { ...prev };
                                                        delete newWarnings[configId];
                                                        return newWarnings;
                                                    });
                                                }
                                            }}
                                            onBlur={(e) => {
                                                // Set default value if field is empty on blur
                                                if (e.target.value === '') {
                                                    updateStorageConfiguration(configId, 'count', 1);
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={disabled}
                                            size="small"
                                            inputProps={{
                                                min: 1,
                                                max: 10,
                                                step: 1
                                            }}
                                            helperText={countWarnings[configId] ? countWarnings[configId].message : "Number of media nodes"}
                                            error={!!countWarnings[configId]}
                                        />
                                    </Grid>
                                    <Grid item xs={6} key={`stack-selection-${configId}`}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Stack"
                                            placeholder="Your Image Name or Select Stack"
                                            value={config.stackName || ''}
                                            onChange={(e) => updateStorageConfiguration(configId, 'stackName', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={disabled}
                                            helperText="Stack image for media storage nodes"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            disabled={disabled}
                                                            onClick={(e) => handleStackMenuOpen(e, configId)}
                                                        >
                                                            <ArrowDropDownIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Popover
                                            open={Boolean(stackMenuAnchorEl[configId])}
                                            anchorEl={stackMenuAnchorEl[configId]}
                                            onClose={() => handleStackMenuClose(configId)}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            PaperProps={{
                                                style: { maxHeight: 300, minWidth: 200 }
                                            }}
                                        >
                                            {stacks && stacks.length > 0 ? (
                                                stacks.map((stack) => (
                                                    <MenuItem 
                                                        key={stack.id || stack._id} 
                                                        onClick={() => handleStackSelect(configId, stack.data.prefix)}
                                                    >
                                                        {stack.data.prefix}{stack.description ? ` - ${stack.description}` : ''}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>
                                                    No stacks available
                                                </MenuItem>
                                            )}
                                        </Popover>
                                    </Grid>

                                    {/* Provider-specific settings */}
                                    {config.providerType === 'volumez' && (
                                        <React.Fragment key={`volumez-settings-${configId}`}>
                                            <Grid item xs={12} key={`volume-settings-header-${configId}`}>
                                                <Divider style={{ margin: '16px 0' }} />
                                                <Typography variant="subtitle2" gutterBottom color="primary">
                                                    Volume Settings
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4} key={`volume-size-${configId}`}>
                                                {(() => {
                                                    const selectedPolicy = config.selectedPolicy;
                                                    const constraints = policyConstraints[config.providerId]?.[selectedPolicy];
                                                    const validation = configurationValidation[config.providerId];
                                                    
                                                    // Get allowed volume sizes for current instance type
                                                    const allowedVolumeSizes = selectedPolicy && selectedPolicy !== 'custom' && constraints
                                                        ? (() => {
                                                            const instanceConfig = constraints.allowedConfigurations?.find(
                                                                c => c.instanceType === config.mediaType
                                                            );
                                                            return instanceConfig?.allowedVolumeSizes || [];
                                                        })()
                                                        : [];
                                                    
                                                    const currentSize = config.volumeConfig?.size;
                                                    const isValidSize = selectedPolicy === 'custom' || 
                                                                       !allowedVolumeSizes.length || 
                                                                       allowedVolumeSizes.includes(currentSize);
                                                    
                                                    const helperText = (() => {
                                                        if (selectedPolicy === 'custom') {
                                                            return 'Any volume size allowed';
                                                        }
                                                        if (allowedVolumeSizes.length > 0) {
                                                            return `Allowed sizes for ${config.mediaType || 'selected instance'}: ${allowedVolumeSizes.join(', ')} GB`;
                                                        }
                                                        if (constraints && config.mediaType) {
                                                            return 'Instance type not found in policy constraints';
                                                        }
                                                        return 'Select instance type to see volume size constraints';
                                                    })();
                                                    
                                                    return (
                                                        <TextField
                                                            fullWidth
                                                            label="Size (GB)"
                                                            type="number"
                                                            value={currentSize !== undefined ? currentSize : ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                updateStorageConfiguration(configId, 'volumeConfig.size', value);
                                                            }}
                                                            onBlur={(e) => {
                                                                // Set default value if field is empty on blur
                                                                if (e.target.value === '') {
                                                                    const defaultSize = allowedVolumeSizes.length > 0 
                                                                        ? Math.min(...allowedVolumeSizes) 
                                                                        : 100;
                                                                    updateStorageConfiguration(configId, 'volumeConfig.size', defaultSize);
                                                                }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={disabled}
                                                            size="small"
                                                            error={!isValidSize}
                                                            helperText={!isValidSize 
                                                                ? `Volume size ${currentSize} GB not allowed by ${selectedPolicy} policy`
                                                                : helperText
                                                            }
                                                            InputProps={{
                                                                endAdornment: selectedPolicy && selectedPolicy !== 'custom' && allowedVolumeSizes.length > 0 && (
                                                                    <Tooltip title={`Allowed sizes: ${allowedVolumeSizes.join(', ')} GB`}>
                                                                        <InfoIcon style={{ color: '#757575', fontSize: '1rem' }} />
                                                                    </Tooltip>
                                                                )
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </Grid>
                                            <Grid item xs={4} key={`volume-base-path-${configId}`}>
                                                <TextField
                                                    fullWidth
                                                    label="Base Mount Path"
                                                    value={config.volumeConfig?.basePath !== undefined ? 
                                                        config.volumeConfig.basePath : 
                                                        '/mnt'
                                                    }
                                                    onChange={(e) => updateStorageConfiguration(configId, 'volumeConfig.basePath', e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    disabled={disabled}
                                                    size="small"
                                                    helperText="Base directory for volume mounts"
                                                />
                                            </Grid>
                                            <Grid item xs={4} key={`volume-prefix-${configId}`}>
                                                <TextField
                                                    fullWidth
                                                    label="Volume Prefix"
                                                    value={config.volumeConfig?.volumePrefix !== undefined ? 
                                                        config.volumeConfig.volumePrefix : 
                                                        (config.name?.toLowerCase().replace(/\s+/g, '-') || 'storage')
                                                    }
                                                    onChange={(e) => updateStorageConfiguration(configId, 'volumeConfig.volumePrefix', e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    disabled={disabled}
                                                    size="small"
                                                    helperText="Prefix for volume mount directories"
                                                />
                                            </Grid>
                                            
                                            {/* Performance Settings - Only shown when 'custom' is selected */}
                                            {config.selectedPolicy === 'custom' && (
                                                <React.Fragment key={`custom-performance-${configId}`}>
                                                    <Grid item xs={12} key={`custom-perf-header-${configId}`}>
                                                        <Box 
                                                            sx={{ 
                                                                backgroundColor: '#f0f8ff', 
                                                                padding: 2, 
                                                                borderRadius: 1,
                                                                border: '1px solid #dee2e6',
                                                                marginTop: 2,
                                                                marginLeft: 2,
                                                                marginRight: 2,
                                                                paddingLeft: 4,
                                                                paddingRight: 4
                                                            }}
                                                        >
                                                            {/* Custom Policy Title */}
                                                            <Typography variant="subtitle1" gutterBottom color="primary" style={{ marginBottom: 16, fontWeight: 600 }}>
                                                                Custom Policy
                                                            </Typography>
                                                            
                                                            {/* Capacity Configuration */}
                                                            <Box sx={{ marginTop: 2, marginBottom: 3 }}>
                                                                <Typography variant="subtitle2" gutterBottom color="primary">
                                                                    Capacity Configuration
                                                                </Typography>
                                                                
                                                                <Grid container spacing={2} style={{ marginTop: 8 }}>
                                                                    <Grid item xs={6}>
                                                                        <FormControl fullWidth size="small" onClick={(e) => e.stopPropagation()}>
                                                                            <InputLabel>Optimization</InputLabel>
                                                                            <Select
                                                                                value={config.capacityConfig?.optimization || 'performance'}
                                                                                onChange={(e) => {
                                                                                    const newValue = e.target.value;
                                                                                    updateStorageConfiguration(configId, 'capacityConfig.optimization', newValue);
                                                                                    
                                                                                    // If Capacity Optimized is selected, apply automatic settings
                                                                                    if (newValue === 'capacity') {
                                                                                        // Set Zone Failures to 0
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', 0);
                                                                                        // Enable Avoid Reads Across Zones
                                                                                        updateStorageConfiguration(configId, 'advancedConfig.localZoneRead', true);
                                                                                    }
                                                                                }}
                                                                                disabled={disabled}
                                                                                label="Optimization"
                                                                            >
                                                                                <MenuItem value="performance">Performance Optimized</MenuItem>
                                                                                <MenuItem value="capacity">Capacity Optimized</MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Reserved Capacity (%)"
                                                                            type="number"
                                                                            value={config.capacityConfig?.reservation !== undefined ? config.capacityConfig.reservation : ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                                updateStorageConfiguration(configId, 'capacityConfig.reservation', value);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateStorageConfiguration(configId, 'capacityConfig.reservation', 20);
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                max: 100,
                                                                                step: 1
                                                                            }}
                                                                            helperText="Percentage of capacity to reserve (0-100%)"
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                            
                                                            <Grid container spacing={2} style={{ marginTop: 4 }}>
                                                                {/* Read Performance Settings */}
                                                                <Grid item xs={12}>
                                                                    <Typography variant="subtitle2" gutterBottom color="primary">
                                                                        Read Performance
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Read IOPS"
                                                                        type="number"
                                                                        value={config.performanceConfig?.readIops !== undefined ? config.performanceConfig.readIops : ''}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                            updateStorageConfiguration(configId, 'performanceConfig.readIops', value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            // Set default value if field is empty on blur
                                                                            if (e.target.value === '') {
                                                                                updateStorageConfiguration(configId, 'performanceConfig.readIops', 8000);
                                                                            }
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={disabled}
                                                                        size="small"
                                                                        inputProps={{
                                                                            min: 1000,
                                                                            max: 10000000,
                                                                            step: 100
                                                                        }}
                                                                        helperText="Input/Output Operations Per Second (1,000-10,000,000)"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Read Bandwidth (MiB/s)"
                                                                        type="number"
                                                                        value={config.performanceConfig?.readBandwidth !== undefined ? config.performanceConfig.readBandwidth : ''}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                            updateStorageConfiguration(configId, 'performanceConfig.readBandwidth', value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            // Set default value if field is empty on blur
                                                                            if (e.target.value === '') {
                                                                                updateStorageConfiguration(configId, 'performanceConfig.readBandwidth', 1000);
                                                                            }
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={disabled}
                                                                        size="small"
                                                                        inputProps={{
                                                                            min: 1,
                                                                            max: 50000,
                                                                            step: 50
                                                                        }}
                                                                        helperText="Data transfer speed in MiB/s (1-50,000)"
                                                                    />
                                                                </Grid>
                                                                
                                                                {/* Write Performance Settings */}
                                                                <Grid item xs={12}>
                                                                    <Typography variant="subtitle2" gutterBottom color="primary" style={{ marginTop: 16 }}>
                                                                        Write Performance
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Write IOPS"
                                                                        type="number"
                                                                        value={config.performanceConfig?.writeIops !== undefined ? config.performanceConfig.writeIops : ''}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                            updateStorageConfiguration(configId, 'performanceConfig.writeIops', value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            // Set default value if field is empty on blur
                                                                            if (e.target.value === '') {
                                                                                updateStorageConfiguration(configId, 'performanceConfig.writeIops', 8000);
                                                                            }
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={disabled}
                                                                        size="small"
                                                                        inputProps={{
                                                                            min: 1000,
                                                                            max: 10000000,
                                                                            step: 100
                                                                        }}
                                                                        helperText="Input/Output Operations Per Second (1,000-10,000,000)"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Write Bandwidth (MiB/s)"
                                                                        type="number"
                                                                        value={config.performanceConfig?.writeBandwidth !== undefined ? config.performanceConfig.writeBandwidth : ''}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                            updateStorageConfiguration(configId, 'performanceConfig.writeBandwidth', value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            // Set default value if field is empty on blur
                                                                            if (e.target.value === '') {
                                                                                updateStorageConfiguration(configId, 'performanceConfig.writeBandwidth', 1000);
                                                                            }
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={disabled}
                                                                        size="small"
                                                                        inputProps={{
                                                                            min: 1,
                                                                            max: 50000,
                                                                            step: 50
                                                                        }}
                                                                        helperText="Data transfer speed in MiB/s (1-50,000)"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                            
                                                            {/* Resiliency Configuration */}
                                                            <Box sx={{ marginTop: 4 }}>
                                                                <Typography variant="subtitle2" gutterBottom color="primary" style={{ marginTop: 16 }}>
                                                                    Resiliency Configuration
                                                                </Typography>
                                                                
                                                                <Grid container spacing={2} style={{ marginTop: 4 }}>
                                                                    <Grid item xs={4}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Media Failures"
                                                                            type="number"
                                                                            value={config.resiliencyConfig?.mediaFailures !== undefined ? config.resiliencyConfig.mediaFailures : ''}
                                                                            onChange={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                // Allow empty value or any number during typing
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.mediaFailures', '');
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    if (!isNaN(numValue)) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.mediaFailures', numValue);
                                                                                        
                                                                                        // Recalculate count based on new resiliency settings
                                                                                        if (config.selectedPolicy && config.selectedPolicy !== 'custom' && config.mediaType) {
                                                                                            const newResiliencyConfig = { ...config.resiliencyConfig, mediaFailures: numValue };
                                                                                            const requiredCount = calculateRequiredCount(
                                                                                                config.selectedPolicy,
                                                                                                config.mediaType,
                                                                                                newResiliencyConfig,
                                                                                                config.providerId
                                                                                            );
                                                                                            updateStorageConfiguration(configId, 'count', requiredCount);
                                                                                            
                                                                                            // Clear any count warnings when resiliency changes and count is auto-updated
                                                                                            setCountWarnings(prev => {
                                                                                                const newWarnings = { ...prev };
                                                                                                delete newWarnings[configId];
                                                                                                return newWarnings;
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.mediaFailures', 0);
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    // Enforce valid range on blur
                                                                                    if (isNaN(numValue) || numValue < 0) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.mediaFailures', 0);
                                                                                    } else if (numValue > 2) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.mediaFailures', 2);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                max: 2,
                                                                                step: 1
                                                                            }}
                                                                            helperText="Disk/memory failures to sustain (0-2)"
                                                                            error={config.resiliencyConfig?.mediaFailures !== undefined && config.resiliencyConfig?.mediaFailures !== '' && (isNaN(parseInt(config.resiliencyConfig?.mediaFailures)) || parseInt(config.resiliencyConfig?.mediaFailures) < 0 || parseInt(config.resiliencyConfig?.mediaFailures) > 2)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Node Failures"
                                                                            type="number"
                                                                            value={config.resiliencyConfig?.nodeFailures !== undefined ? config.resiliencyConfig.nodeFailures : ''}
                                                                            onChange={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                // Allow empty value or any number during typing
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.nodeFailures', '');
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    if (!isNaN(numValue)) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.nodeFailures', numValue);
                                                                                        
                                                                                        // Recalculate count based on new resiliency settings
                                                                                        if (config.selectedPolicy && config.selectedPolicy !== 'custom' && config.mediaType) {
                                                                                            const newResiliencyConfig = { ...config.resiliencyConfig, nodeFailures: numValue };
                                                                                            const requiredCount = calculateRequiredCount(
                                                                                                config.selectedPolicy,
                                                                                                config.mediaType,
                                                                                                newResiliencyConfig,
                                                                                                config.providerId
                                                                                            );
                                                                                            updateStorageConfiguration(configId, 'count', requiredCount);
                                                                                            
                                                                                            // Clear any count warnings when resiliency changes and count is auto-updated
                                                                                            setCountWarnings(prev => {
                                                                                                const newWarnings = { ...prev };
                                                                                                delete newWarnings[configId];
                                                                                                return newWarnings;
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.nodeFailures', 0);
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    // Enforce valid range on blur
                                                                                    if (isNaN(numValue) || numValue < 0) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.nodeFailures', 0);
                                                                                    } else if (numValue > 2) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.nodeFailures', 2);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                max: 2,
                                                                                step: 1
                                                                            }}
                                                                            helperText="EC2 instance failures to sustain (0-2)"
                                                                            error={config.resiliencyConfig?.nodeFailures !== undefined && config.resiliencyConfig?.nodeFailures !== '' && (isNaN(parseInt(config.resiliencyConfig?.nodeFailures)) || parseInt(config.resiliencyConfig?.nodeFailures) < 0 || parseInt(config.resiliencyConfig?.nodeFailures) > 2)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Zone Failures"
                                                                            type="number"
                                                                            value={config.resiliencyConfig?.zoneFailures !== undefined ? config.resiliencyConfig.zoneFailures : ''}
                                                                            onChange={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                // Allow empty value or any number during typing
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', '');
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    if (!isNaN(numValue)) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', numValue);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                if (inputValue === '') {
                                                                                    updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', 0);
                                                                                } else {
                                                                                    const numValue = parseInt(inputValue);
                                                                                    // Enforce valid range on blur
                                                                                    if (isNaN(numValue) || numValue < 0) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', 0);
                                                                                    } else if (numValue > 2) {
                                                                                        updateStorageConfiguration(configId, 'resiliencyConfig.zoneFailures', 2);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled || (config.capacityConfig?.optimization === 'capacity')}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 0,
                                                                                max: 2,
                                                                                step: 1
                                                                            }}
                                                                            helperText={config.capacityConfig?.optimization === 'capacity' ? 
                                                                                "Fixed at 0 for Capacity Optimized" : 
                                                                                "Availability zone failures to sustain (0-2)"
                                                                            }
                                                                            error={config.resiliencyConfig?.zoneFailures !== undefined && config.resiliencyConfig?.zoneFailures !== '' && (isNaN(parseInt(config.resiliencyConfig?.zoneFailures)) || parseInt(config.resiliencyConfig?.zoneFailures) < 0 || parseInt(config.resiliencyConfig?.zoneFailures) > 2)}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                            
                                                            {/* Latency Configuration */}
                                                            <Box sx={{ marginTop: 4 }}>
                                                                <Typography variant="subtitle2" gutterBottom color="primary" style={{ marginTop: 16 }}>
                                                                    Latency Configuration
                                                                </Typography>
                                                                
                                                                <Grid container spacing={2} style={{ marginTop: 8 }}>
                                                                    <Grid item xs={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Write Latency (microseconds)"
                                                                            type="number"
                                                                            value={config.performanceConfig?.latencyWrite !== undefined ? config.performanceConfig.latencyWrite : ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                                updateStorageConfiguration(configId, 'performanceConfig.latencyWrite', value);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateStorageConfiguration(configId, 'performanceConfig.latencyWrite', 500);
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 1,
                                                                                step: 1
                                                                            }}
                                                                            helperText="Write latency in microseconds (minimum: 1)"
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Read Latency (microseconds)"
                                                                            type="number"
                                                                            value={config.performanceConfig?.latencyRead !== undefined ? config.performanceConfig.latencyRead : ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                                                                updateStorageConfiguration(configId, 'performanceConfig.latencyRead', value);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateStorageConfiguration(configId, 'performanceConfig.latencyRead', 500);
                                                                                }
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={disabled}
                                                                            size="small"
                                                                            inputProps={{
                                                                                min: 1,
                                                                                step: 1
                                                                            }}
                                                                            helperText="Read latency in microseconds (minimum: 1)"
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                            
                                                            {/* Advanced Configuration */}
                                                            <Divider style={{ margin: '24px 0 16px 0' }} />
                                                            <Box sx={{ marginTop: 2 }}>
                                                                <Typography variant="subtitle2" gutterBottom color="primary" style={{ marginTop: 16 }}>
                                                                    Advanced Configuration
                                                                </Typography>
                                                                
                                                                <Grid container spacing={2} style={{ marginTop: 8 }}>
                                                                    <Grid item xs={6}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Switch
                                                                                    checked={config.advancedConfig?.localZoneRead !== undefined ? config.advancedConfig.localZoneRead : true}
                                                                                    onChange={(e) => updateStorageConfiguration(configId, 'advancedConfig.localZoneRead', e.target.checked)}
                                                                                    disabled={disabled || (config.capacityConfig?.optimization === 'capacity')}
                                                                                />
                                                                            }
                                                                            label={config.capacityConfig?.optimization === 'capacity' ? 
                                                                                "Avoid Reads Across Zones (Auto-enabled for Capacity Optimized)" : 
                                                                                "Avoid Reads Across Zones"
                                                                            }
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Switch
                                                                                    checked={config.advancedConfig?.encryption !== undefined ? config.advancedConfig.encryption : false}
                                                                                    onChange={(e) => updateStorageConfiguration(configId, 'advancedConfig.encryption', e.target.checked)}
                                                                                    disabled={disabled}
                                                                                />
                                                                            }
                                                                            label="Encryption at Source"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                </React.Fragment>
                                            )}
                                            
                                        </React.Fragment>
                                    )}
                                    
                                    {/* Update Volumes Button for this configuration */}
                                    {config.providerType === 'volumez' && hasAttachedVolumesForConfig(configId) && (
                                        <Grid item xs={12} key={`update-volumes-${configId}`}>
                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    onClick={() => handleUpdateVolumesForConfig(configId)}
                                                    disabled={!hasUnsavedChangesForConfig(configId) || isUpdatingVolumes}
                                                    variant="contained"
                                                    size="medium"
                                                    sx={{
                                                        minHeight: '36px',
                                                        minWidth: '240px',
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        backgroundColor: hasUnsavedChangesForConfig(configId) && !isUpdatingVolumes ? '#1976d2' : '#bdbdbd',
                                                        color: hasUnsavedChangesForConfig(configId) && !isUpdatingVolumes ? 'white' : '#757575',
                                                        '&:hover': {
                                                            backgroundColor: hasUnsavedChangesForConfig(configId) && !isUpdatingVolumes ? '#1565c0' : '#bdbdbd'
                                                        },
                                                        '&:disabled': {
                                                            backgroundColor: '#bdbdbd',
                                                            color: '#757575'
                                                        }
                                                    }}
                                                >
                                                    {isUpdatingVolumes ? (
                                                        <>
                                                            <span className={classes.spinnerIcon} style={{ marginRight: '8px' }}></span>
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        'Update current attached volumes'
                                                    )}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Collapse>
                    </Paper>
                            );
                        })}

                        {/* No configurations message */}
                        {storageConfigurations.length === 0 && (
                            <Box 
                                display="flex" 
                                flexDirection="column" 
                                alignItems="center" 
                                justifyContent="center"
                                padding={0}
                                textAlign="center"
                            >
                                <Typography variant="body1" color="textSecondary" gutterBottom>
                                    No data infrastructure configured
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Select a provider from the dropdown above and click "Add" to configure high-performance data infrastructure for your cluster.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </CardContent>
            
            {/* Delete Storage Configuration Confirmation Dialog */}
            <Dialog
                open={confirmDeleteDialogOpen}
                onClose={closeConfirmDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Delete Storage Configuration</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this storage configuration? All current media nodes and attached volumes will be removed. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="flex-end" spacing={2}>
                        <Grid item>
                            <Button color="secondary" onClick={closeConfirmDeleteDialog}>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="primary" onClick={confirmDeleteStorageConfiguration} autoFocus>
                                Delete
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>

            {/* Policy Details Dialog */}
            <PolicyDetailsDialog
                open={policyDetailsDialogOpen}
                onClose={() => {
                    setPolicyDetailsDialogOpen(false);
                    setPolicyDetailsProviderId(null);
                }}
                policies={policyDetailsProviderId ? providerPolicies[policyDetailsProviderId] : []}
                providerName={policyDetailsProviderId ? 
                    providers.find(p => p.id === policyDetailsProviderId)?.name || 'Storage Provider' 
                    : 'Storage Provider'
                }
            />
        </Card>
    );
};

export default StorageConfigurationSection;
